/**
 * Packer stock-audit analytics — `/api/bi/audits/*`.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';

export interface AuditsOverview {
  range: { start: string; end: string };
  caveat: string;
  kpis: {
    sessions: number;
    completed_sessions: number;
    completion_rate: number;
    shift_compliance: number;
    expected_sessions: number;
    stores: number;
    packers: number;
    skus_touched: number;
    lines: number;
    expected_units: number;
    scanned_units: number;
    shortfall_units: number;
    overcount_units: number;
    shortfall_lines: number;
    overcount_lines: number;
  };
  by_day: Array<{ day: string | null; audits: number; completed: number }>;
  by_store: Array<{
    store_id: number;
    store_name: string;
    audits: number;
    completed: number;
  }>;
}

export interface AuditsVariance {
  range: { start: string; end: string };
  caveat: string;
  by_audit_type: Array<{
    audit_type: string;
    lines: number;
    shortfall_units: number;
    overcount_units: number;
    shortfall_lines: number;
    overcount_lines: number;
  }>;
  sku_leaders: Array<{
    product_id: number;
    product_name: string;
    store_id: number;
    store_name: string;
    lines: number;
    audits: number;
    shortfall_units: number;
    overcount_units: number;
  }>;
}

export interface AuditsPackers {
  range: { start: string; end: string };
  rows: Array<{
    packer_id: number | null;
    packer_name: string;
    store_id: number;
    store_name: string;
    sessions: number;
    lines: number;
    match_rate: number;
    shortfall_units: number;
    overcount_units: number;
    shortfall_lines: number;
  }>;
}

export interface AuditsCoverage {
  range: { start: string; end: string };
  rotations: Array<{
    store_id: number;
    store_name: string;
    current_round: number;
    last_product_id: number | null;
    skus_in_stock: number;
    skus_audited_in_range: number;
    coverage_rate: number;
  }>;
  wrap_events_in_range: number;
  never_audited_sample: Array<{
    store_id: number;
    store_name: string;
    product_id: number;
    product_name: string;
    stock_quantity: number;
  }>;
}

export interface AuditsSessions {
  range: { start: string; end: string };
  results: Array<{
    id: number;
    store_id: number;
    store_name: string | null;
    packer_id: number;
    packer_name: string | null;
    created_at: string | null;
    completed_at: string | null;
    wrapped_around: boolean;
    item_count: number;
    shortfall_units: number;
    overcount_units: number;
  }>;
  items: Array<{
    id: number;
    product_id: number;
    product_name: string;
    audit_type: string;
    expected_quantity: number;
    scanned_quantity: number;
    signed_variance: number;
    is_completed: boolean;
  }>;
  pagination: { page: number; page_size: number; total: number; total_pages: number };
}

export interface AuditsSkuDetail {
  range: { start: string; end: string };
  product: { id: number; name: string | null };
  totals: {
    lines: number;
    audits: number;
    expected_units: number;
    scanned_units: number;
    shortfall_units: number;
    overcount_units: number;
    match_rate: number;
  };
  lines: Array<{
    id: number;
    audit_id: number;
    created_at: string | null;
    store_id: number;
    store_name: string | null;
    packer_name: string | null;
    audit_type: string;
    expected_quantity: number;
    scanned_quantity: number;
    signed_variance: number;
  }>;
  by_day: Array<{
    day: string | null;
    lines: number;
    shortfall_units: number;
    overcount_units: number;
  }>;
  by_packer: Array<{
    packer_id: number | null;
    packer_name: string;
    lines: number;
    shortfall_units: number;
    overcount_units: number;
  }>;
}

export interface AuditsPackerDetail {
  range: { start: string; end: string };
  packer: { id: number; name: string };
  totals: {
    lines: number;
    sessions: number;
    skus: number;
    shortfall_units: number;
    overcount_units: number;
    match_rate: number;
  };
  sessions: Array<{
    audit_id: number;
    created_at: string | null;
    completed_at: string | null;
    store_id: number;
    store_name: string | null;
    item_count: number;
    shortfall_units: number;
    overcount_units: number;
    match_rate: number;
  }>;
  worst_skus: Array<{
    product_id: number;
    product_name: string;
    lines: number;
    shortfall_units: number;
    overcount_units: number;
  }>;
  by_day: Array<{
    day: string | null;
    lines: number;
    match_rate: number;
    shortfall_units: number;
  }>;
  /** Null when the packer record has no linked user account. */
  lost_items: {
    user_id: number;
    reports: number;
    units: number;
    approved: number;
    rejected: number;
    pending: number;
  } | null;
}

export interface AuditsCoverageStore {
  range: { start: string; end: string };
  store: { id: number; name: string | null };
  rotation: { current_round: number; last_product_id: number | null } | null;
  totals: {
    skus_in_stock: number;
    skus_audited: number;
    skus_audited_in_stock: number;
    skus_never_audited: number;
    coverage_rate: number;
  };
  audited: Array<{
    product_id: number;
    product_name: string;
    lines: number;
    audits: number;
    shortfall_units: number;
    overcount_units: number;
    last_audited: string | null;
  }>;
  never_audited: Array<{
    product_id: number;
    product_name: string;
    stock_quantity: number;
    stock_value: number | null;
  }>;
}

function rangeParams(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return {
    start: f.start ?? undefined,
    end: f.end ?? undefined,
    store_id: f.storeId ?? undefined,
  };
}

export function useAuditsOverview(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return useQuery({
    queryKey: ['bi', 'audits', 'overview', f.start ?? null, f.end ?? null, f.storeId ?? null],
    queryFn: () => get<AuditsOverview>('/api/bi/audits/overview/', { params: rangeParams(f) }),
  });
}

export function useAuditsVariance(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return useQuery({
    queryKey: ['bi', 'audits', 'variance', f.start ?? null, f.end ?? null, f.storeId ?? null],
    queryFn: () => get<AuditsVariance>('/api/bi/audits/variance/', { params: rangeParams(f) }),
  });
}

export function useAuditsPackers(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return useQuery({
    queryKey: ['bi', 'audits', 'packers', f.start ?? null, f.end ?? null, f.storeId ?? null],
    queryFn: () => get<AuditsPackers>('/api/bi/audits/packers/', { params: rangeParams(f) }),
  });
}

export function useAuditsCoverage(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return useQuery({
    queryKey: ['bi', 'audits', 'coverage', f.start ?? null, f.end ?? null, f.storeId ?? null],
    queryFn: () => get<AuditsCoverage>('/api/bi/audits/coverage/', { params: rangeParams(f) }),
  });
}

export function useAuditsSessions(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
  page?: number;
  pageSize?: number;
  auditId?: number | null;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'audits',
      'sessions',
      f.start ?? null,
      f.end ?? null,
      f.storeId ?? null,
      f.page ?? 1,
      f.pageSize ?? 25,
      f.auditId ?? null,
    ],
    queryFn: () =>
      get<AuditsSessions>('/api/bi/audits/sessions/', {
        params: {
          ...rangeParams(f),
          page: f.page ?? 1,
          page_size: f.pageSize ?? 25,
          audit_id: f.auditId ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });
}

/**
 * Drill-downs. All three are `enabled` on their id so the dialog can mount
 * before a target is chosen without firing a request.
 */
export function useAuditsSkuDetail(f: {
  productId: number | null;
  storeId?: number | null;
  start?: string | null;
  end?: string | null;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'audits',
      'sku',
      f.productId ?? null,
      f.storeId ?? null,
      f.start ?? null,
      f.end ?? null,
    ],
    queryFn: () =>
      get<AuditsSkuDetail>('/api/bi/audits/sku/', {
        params: {
          product_id: f.productId,
          store_id: f.storeId ?? undefined,
          start: f.start ?? undefined,
          end: f.end ?? undefined,
        },
      }),
    enabled: f.productId != null,
  });
}

export function useAuditsPackerDetail(f: {
  packerId: number | null;
  storeId?: number | null;
  start?: string | null;
  end?: string | null;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'audits',
      'packer',
      f.packerId ?? null,
      f.storeId ?? null,
      f.start ?? null,
      f.end ?? null,
    ],
    queryFn: () =>
      get<AuditsPackerDetail>('/api/bi/audits/packer/', {
        params: {
          packer_id: f.packerId,
          store_id: f.storeId ?? undefined,
          start: f.start ?? undefined,
          end: f.end ?? undefined,
        },
      }),
    enabled: f.packerId != null,
  });
}

export function useAuditsCoverageStore(f: {
  storeId: number | null;
  start?: string | null;
  end?: string | null;
}) {
  return useQuery({
    queryKey: ['bi', 'audits', 'coverage-store', f.storeId ?? null, f.start ?? null, f.end ?? null],
    queryFn: () =>
      get<AuditsCoverageStore>('/api/bi/audits/coverage/store/', {
        params: {
          store_id: f.storeId,
          start: f.start ?? undefined,
          end: f.end ?? undefined,
        },
      }),
    enabled: f.storeId != null,
  });
}
